/**
 * Single source of truth for which AdminLayout header banner is active.
 * States are mutually exclusive — only one can be selected at a time.
 *
 * Change `ACTIVE_HEADER_BANNER` to switch between promo, maintenance, or none.
 * Both rendering and promo API fetching are driven from this value.
 *
 * When maintenance mode is active, change `ACTIVE_MAINTENANCE_BANNER_VARIANT`
 * to select which maintenance banner view is shown.
 */
export const HEADER_BANNER_MODES = {
  PROMO: "promo",
  MAINTENANCE: "maintenance",
  NONE: "none",
} as const;

export type HeaderBannerMode =
  (typeof HEADER_BANNER_MODES)[keyof typeof HEADER_BANNER_MODES];

export const ACTIVE_HEADER_BANNER: HeaderBannerMode =
  HEADER_BANNER_MODES.PROMO;

function resolveBannerModeFlags(mode: HeaderBannerMode) {
  return {
    isPromoBannerMode: mode === HEADER_BANNER_MODES.PROMO,
    isMaintenanceBannerMode: mode === HEADER_BANNER_MODES.MAINTENANCE,
    isHeaderBannerDisabled: mode === HEADER_BANNER_MODES.NONE,
  } as const;
}

export const {
  isPromoBannerMode,
  isMaintenanceBannerMode,
  isHeaderBannerDisabled,
} = resolveBannerModeFlags(ACTIVE_HEADER_BANNER);

export const MAINTENANCE_BANNER_VARIANTS = {
  CHECKOUT_UNAVAILABLE: {
    bannerStyle: "bg-[#FFABAB]",
    title: "Checkout is temporarily unavailable while we perform upgrades.",
    subtitle: "We'll be back shortly.",
  },
  SYSTEM_UPGRADE: {
    bannerStyle: "bg-[#FFE1D259]",
    title: "We're currently upgrading our system, checkout will be back soon.",
    subtitle: null,
  },
} as const;

export type MaintenanceBannerVariantKey =
  keyof typeof MAINTENANCE_BANNER_VARIANTS;

export type MaintenanceBannerVariant =
  (typeof MAINTENANCE_BANNER_VARIANTS)[MaintenanceBannerVariantKey];

export const ACTIVE_MAINTENANCE_BANNER_VARIANT: MaintenanceBannerVariantKey =
  "SYSTEM_UPGRADE";

export const activeMaintenanceBannerVariant: MaintenanceBannerVariant =
  MAINTENANCE_BANNER_VARIANTS[ACTIVE_MAINTENANCE_BANNER_VARIANT];
